import { atom, selector } from 'recoil';
import type { SegmentationData } from '~/backend/segmentationMetrics';
import type { QaCoverage } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/AdaptiveReplanningStep/qaCoverage';
import type { DoseId } from '~/domain/changes/ids';
import type { WorkflowTypes } from '~/state/atoms/viewerAtoms';

export type ReplanningStageId =
    | 'selection'
    | 'synthetic-imaging'
    | 'register'
    | 'registration-qa'
    | 'review';

export const replanningStageState = atom<ReplanningStageId>({
    key: 'replanningStage',
    default: 'selection',
});

// A planned structure and the model structure Contour+ fills it with.
export interface ContourAssignment {
    readonly name: string;
    readonly modelRoiName: string;
}

export interface ContourModelChoice {
    readonly modelName: string;
    // Undefined for the model's default customization.
    readonly customizationName?: string;
}

export interface ReplanningRunOutputs {
    readonly sourceUid?: string;
    readonly pendingPropagatedUid?: string;
    readonly propagatedUid?: string;
    readonly propagatedDoseUid?: string;
    // The structures the run promised, by where each comes from.
    readonly propagatedRoiNames?: readonly string[];
    readonly contourRois?: readonly ContourAssignment[];
    // The Contour+ model and customization the run's assignments were paired against.
    readonly contourModel?: ContourModelChoice;
    // The Contour+ job on the target scan; absent when none was started.
    readonly contourJobId?: string;
    // The Contour+ job on the planning scan, run alongside for QA.
    readonly contourSourceJobId?: string;
    // The scans the run was made between, as Plan & Target named them.
    readonly planningSeriesUid?: string;
    readonly targetSeriesUid?: string;
    // Contour+ structures the model did not produce, kept as propagated.
    readonly contourFallbackNames?: readonly string[];
}

export const replanningRunOutputsState = atom<ReplanningRunOutputs>({
    key: 'replanningRunOutputs',
    default: {},
});

export interface ReplanningRunSummary {
    readonly modelName: string;
    readonly propagatedCount: number;
    readonly segmentedCount: number;
    readonly initRigidSopInstanceUid?: string;
    // The planning dose the run carried: the prior dose Review compares against.
    readonly priorDoseSopInstanceUid?: string;
}

export const replanningRunSummaryState = atom<ReplanningRunSummary | undefined>(
    {
        key: 'replanningRunSummary',
        default: undefined,
    }
);

export const replanningRunRegistrationsState = atom<readonly string[]>({
    key: 'replanningRunRegistrations',
    default: [],
});

export const replanningReviewedDosesState = atom<readonly string[]>({
    key: 'replanningReviewedDoses',
    default: [],
});

export const replanningCompareViewState = atom<boolean>({
    key: 'replanningCompareView',
    default: false,
});

export interface ReplanningPreparation {
    readonly generateSynthetic: boolean;
    readonly syntheticModelName?: string;
    readonly compositeCbct: boolean;
}

export const replanningPreparationState = atom<ReplanningPreparation>({
    key: 'replanningPreparation',
    default: { generateSynthetic: false, compositeCbct: false },
});

export const replanningEditingStructuresState = atom<boolean>({
    key: 'replanningEditingStructures',
    default: false,
});

// True while the Review step is on screen, whose approval control signs off the propagated set.
export const replanningReviewActiveState = atom<boolean>({
    key: 'replanningReviewActive',
    default: false,
});

export type RegisterTab = 'process' | 'visualization';

export const replanningRegisterTabState = atom<RegisterTab>({
    key: 'replanningRegisterTab',
    default: 'process',
});

export const replanningVisibleRegisterTabState = selector<RegisterTab>({
    key: 'replanningVisibleRegisterTab',
    get: ({ get }) =>
        get(replanningRegisterTabState) === 'visualization' &&
        get(replanningRunOutputsState).propagatedUid
            ? 'visualization'
            : 'process',
});

export type QaRunPhase =
    | 'idle'
    | 'segmenting'
    | 'ready'
    | 'failed'
    | 'cancelled';

export interface QaRunState {
    readonly phase: QaRunPhase;
    readonly jobIds: { source?: string; target?: string };
    readonly forRun?: string;
}

export const replanningQaRunState = atom<QaRunState>({
    key: 'replanningQaRun',
    default: { phase: 'idle', jobIds: {} },
});

// The run's target-scan Contour+ set, added as a session-only derived set so the QA target panel can show it.
export interface QaContourStructures {
    readonly forRun: string;
    readonly structureSetUid: string;
}

export const replanningQaContourSetState = atom<
    QaContourStructures | undefined
>({
    key: 'replanningQaContourSet',
    default: undefined,
});

// The propagated set whose ROI names and colors have been reconciled to the source's, so it runs once per run.
export const replanningReconciledStructuresState = atom<string | undefined>({
    key: 'replanningReconciledStructures',
    default: undefined,
});

export interface SyntheticRunRecord {
    readonly type: WorkflowTypes;
    readonly modelName?: string;
    readonly composite: boolean;
}

export const replanningSyntheticRunState = atom<SyntheticRunRecord | undefined>(
    {
        key: 'replanningSyntheticRun',
        default: undefined,
    }
);

export const replanningPropagationSubmittingState = atom<boolean>({
    key: 'replanningPropagationSubmitting',
    default: false,
});

// Keyed by the two segmentations and the registration it measures.
export type QaMeasurement =
    | { readonly status: 'measuring'; readonly key: string }
    | {
          readonly status: 'ready';
          readonly key: string;
          readonly rows: readonly SegmentationData[];
          readonly coverage: QaCoverage;
      }
    | {
          readonly status: 'failed';
          readonly key: string;
          // Why, when the frontend refused before measuring; the generic reason otherwise.
          readonly messageId?: string;
          // The backend's own words, shown after the reason.
          readonly detail?: string;
      };

export const replanningQaMeasurementState = atom<QaMeasurement | undefined>({
    key: 'replanningQaMeasurement',
    default: undefined,
});

// Chosen on Plan & Target; later stages change what is displayed, never this.
export interface ReplanningScans {
    readonly sourceSeriesUid?: string;
    readonly targetSeriesUid?: string;
}

export const replanningScansState = atom<ReplanningScans>({
    key: 'replanningScans',
    default: {},
});

// The registration Review shows the target through, chosen once per run.
export interface ReviewTransformChoice {
    readonly forRun?: string;
    readonly sopInstanceUid?: string;
}

export const replanningReviewTransformState = atom<ReviewTransformChoice>({
    key: 'replanningReviewTransform',
    default: {},
});

// Multiplies the dose Review shows on the target, always applied to the original dose; 1 leaves it as it is.
export const replanningDoseScaleState = atom<number>({
    key: 'replanningDoseScale',
    default: 1,
});

// One dose per transform computed for the target, keyed by everything it was computed from.
export interface MappedDoseRecord {
    readonly key: string;
    readonly transformUid: string;
    readonly doseId: DoseId;
    readonly totalVoxels: number;
    // Voxels the transform does not reach, shown as 0 Gy.
    readonly unmappedVoxels: number;
    // Voxels outside the prior dose grid, 0 Gy as nothing was planned there.
    readonly outsideSourceVoxels: number;
}

export const replanningMappedDosesState = atom<readonly MappedDoseRecord[]>({
    key: 'replanningMappedDoses',
    default: [],
});

export type DoseMappingStatus =
    | { readonly status: 'mapping'; readonly key: string }
    | {
          readonly status: 'failed';
          readonly key: string;
          readonly messageId: string;
      };

export const replanningDoseMappingState = atom<DoseMappingStatus | undefined>({
    key: 'replanningDoseMapping',
    default: undefined,
});

// The planning structures moved by the rigid onto the target scan, kept for the run while Review is open.
export interface RigidStructuresRecord {
    readonly key: string;
    readonly structureSetUid: string;
    // Planning structures that had no contour to move.
    readonly skippedRoiCount: number;
}

export const replanningRigidStructuresState = atom<
    RigidStructuresRecord | undefined
>({
    key: 'replanningRigidStructures',
    default: undefined,
});

export type RigidStructuresStatus =
    | { readonly status: 'building'; readonly key: string }
    | {
          readonly status: 'failed';
          readonly key: string;
          readonly messageId: string;
      };

export const replanningRigidStructuresStatusState = atom<
    RigidStructuresStatus | undefined
>({
    key: 'replanningRigidStructuresStatus',
    default: undefined,
});

export const replanningDvhComparisonOpenState = atom<boolean>({
    key: 'replanningDvhComparisonOpen',
    default: false,
});
